import React, { useState, useRef, useEffect } from 'react';
import styles from '../../styles/formComponent/InputBox.module.scss';

const InputBox = ({
  type = 'text',
  label,
  name,
  value = '',
  onChange,
  onBlur,
  onFocus,
  placeholder,
  required = false,
  disabled = false,
  error,
  helperText,
  variant = 'outlined',
  size = 'medium',
  multiline = false,
  rows = 4,
  className = '',
  autoFocus = false,
  maxLength,
  minLength,
  autoComplete,
  readOnly = false,
  showValidationIcon = true,
  validationState, // 'valid', 'invalid', 'validating'
  customActionOnChange, // Custom action to perform on change
  startIcon, // Icon to display at the start of the input
  onStartIconClick, // Click handler for the start icon
  endIcon, // Icon to display at the end of the input
  onEndIconClick, // Click handler for the end icon
  customClassName='', // Custom class name for the input
  ...props
}) => {
  const [focused, setFocused] = useState(false);
  const [hasValue, setHasValue] = useState(Boolean(value));
  const inputRef = useRef(null);

  useEffect(() => {
    setHasValue(Boolean(value));
  }, [value]);

  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus]);

  const handleFocus = (e) => {
    setFocused(true);
    if (onFocus) {
      onFocus(e);
    }
  };

  const handleBlur = (e) => {
    setFocused(false);
    if (onBlur) {
      onBlur(e);
    }
  };

  const handleChange = (e) => {
    const newValue = e.target.value;
    setHasValue(Boolean(newValue));
    
    // Execute custom action first if provided
    if (customActionOnChange) {
      customActionOnChange(e, newValue);
    }
    
    // Then execute the regular onChange
    if (onChange) {
      onChange(e);
    }
  };

  const getInputClasses = () => {
    let classes = multiline ? styles.inputFieldMultiline : styles.inputField;
    
    if (error) classes += ` ${styles.error}`;
    if (hasValue) classes += ` ${styles.filled}`;
    if (label) classes += ` ${styles.hasLabel}`;
    if (readOnly) classes += ` ${styles.readOnly}`;
    if (validationState === 'valid') classes += ` ${styles.valid}`;
    if (validationState === 'invalid') classes += ` ${styles.invalid}`;
    if (validationState === 'validating') classes += ` ${styles.validating}`;
    
    return classes;
  };

  const getLabelClasses = () => {
    const labelClass = multiline ? styles.floatingLabelMultiline : styles.floatingLabel;
    let classes = labelClass;
    
    if (focused || hasValue) classes += ` ${styles.filled}`;
    if (focused) classes += ` ${styles.focused}`;
    if (error) classes += ` ${styles.error}`;
    if (disabled) classes += ` ${styles.disabled}`;
    
    return classes;
  };

  const getWrapperClasses = () => {
    let classes = `${styles.inputWrapper}`;
    if (variant === 'filled') classes += ` ${styles.filled}`;
    if (variant === 'outlined') classes += ` ${styles.outlined}`;
    if (size === 'small') classes += ` ${styles.small}`;
    if (className) classes += ` ${className}`;
    if (showValidationIcon && validationState) classes += ` ${styles.hasValidationIcon}`;
    if (startIcon) classes += ` ${styles.hasStartIcon}`;
    if (endIcon) classes += ` ${styles.hasEndIcon}`;
    
    return classes;
  };

  const renderValidationIcon = () => {
    if (!showValidationIcon || !validationState) return null;
    
    switch (validationState) {
      case 'valid':
        return (
          <div className={styles.validationIcon}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M13.5 4.5L6 12L2.5 8.5" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
        );
      case 'invalid':
        return (
          <div className={styles.validationIcon}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M12 4L4 12M4 4L12 12" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
        );
      case 'validating':
        return (
          <div className={styles.validationIcon}>
            <div className={styles.spinner}></div>
          </div>
        );
      default:
        return null;
    }
  };

  const renderStartIcon = () => {
    if (!startIcon) return null;
    
    const handleIconClick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (onStartIconClick && !disabled) {
        onStartIconClick(e);
      }
    };

    return (
      <div 
        className={`${styles.startIcon} ${onStartIconClick && !disabled ? styles.clickable : ''}`}
        onClick={handleIconClick}
        style={{ pointerEvents: disabled ? 'none' : 'auto' }}
      >
        {typeof startIcon === 'string' ? (
          <span>{startIcon}</span>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {startIcon}
          </div>
        )}
      </div>
    );
  };

  const renderEndIcon = () => {
    if (!endIcon) return null;
    
    const handleIconClick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (onEndIconClick && !disabled) {
        onEndIconClick(e);
      }
    };

    return (
      <div 
        className={`${styles.endIcon} ${onEndIconClick && !disabled ? styles.clickable : ''}`}
        onClick={handleIconClick}
        style={{ pointerEvents: disabled ? 'none' : 'auto' }}
      >
        {typeof endIcon === 'string' ? (
          <span>{endIcon}</span>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {endIcon}
          </div>
        )}
      </div>
    );
  };

  const InputComponent = multiline ? 'textarea' : 'input';

  return (
    <div className={getWrapperClasses()}>
      <div className={styles.inputContainer}>
        {renderStartIcon()}
        <InputComponent
          ref={inputRef}
          type={multiline ? undefined : type}
          name={name}
          value={value}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          readOnly={readOnly}
          autoComplete={autoComplete}
          className={`${getInputClasses()} ${customClassName}`}
          rows={multiline ? rows : undefined}
          maxLength={maxLength}
          minLength={minLength}
          {...props}
        />
        {label && (
          <label className={getLabelClasses()}>
            {label}
            {required && <span style={{ color: '#dc2626' }}> *</span>}
          </label>
        )}
        {renderValidationIcon()}
        {renderEndIcon()}
      </div>
      {error && (
        <div className={styles.errorMessage}>{error}</div>
      )}
      {helperText && !error && (
        <div className={styles.helperText}>{helperText}</div>
      )}
    </div>
  );
};

export default InputBox;